import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GitBranch, 
  Plus, 
  Trash2, 
  ArrowRight, 
  Settings, 
  Eye,
  AlertCircle,
  CheckCircle,
  X,
  Edit3,
  Save,
  RotateCcw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../../hooks/useToast';

interface ConditionalRule {
  id: string;
  sourceQuestionId: string;
  sourceAnswerId: string;
  targetQuestionId: string;
  condition: 'equals' | 'not_equals' | 'contains' | 'score_above' | 'score_below';
  value?: string | number;
  action: 'show' | 'hide' | 'jump_to' | 'end_quiz';
  isActive: boolean;
  description?: string;
}

interface ConditionalQuestionsProps {
  questions: Array<{
    id: string;
    question_text: string;
    question_type: string;
    sort_order: number;
    answers?: Array<{
      id: string;
      answer_text: string;
      is_correct: boolean;
    }>;
  }>;
  conditionalRules: ConditionalRule[];
  onRulesChange: (rules: ConditionalRule[]) => void;
}

const ConditionalQuestions: React.FC<ConditionalQuestionsProps> = ({
  questions,
  conditionalRules,
  onRulesChange
}) => {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ConditionalRule | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  
  const [ruleForm, setRuleForm] = useState<Partial<ConditionalRule>>({
    sourceQuestionId: '',
    sourceAnswerId: '',
    targetQuestionId: '',
    condition: 'equals',
    action: 'show',
    isActive: true,
    description: ''
  });

  const conditionOptions = [
    { value: 'equals', label: 'Égal à', description: 'La réponse correspond exactement' },
    { value: 'not_equals', label: 'Différent de', description: 'La réponse ne correspond pas' },
    { value: 'contains', label: 'Contient', description: 'La réponse contient le texte' },
    { value: 'score_above', label: 'Score supérieur à', description: 'Score de la question > valeur' },
    { value: 'score_below', label: 'Score inférieur à', description: 'Score de la question < valeur' }
  ];

  const actionOptions = [
    { value: 'show', label: 'Afficher', description: 'Affiche la question cible', icon: Eye, color: 'text-green-400' },
    { value: 'hide', label: 'Masquer', description: 'Masque la question cible', icon: X, color: 'text-red-400' },
    { value: 'jump_to', label: 'Aller à', description: 'Passe directement à la question', icon: ArrowRight, color: 'text-blue-400' },
    { value: 'end_quiz', label: 'Terminer', description: 'Termine le quiz', icon: CheckCircle, color: 'text-purple-400' }
  ];

  const resetForm = () => {
    setRuleForm({
      sourceQuestionId: '',
      sourceAnswerId: '',
      targetQuestionId: '',
      condition: 'equals',
      action: 'show',
      isActive: true,
      description: ''
    });
    setEditingRule(null);
  };

  const openEditDialog = (rule?: ConditionalRule) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({ ...rule });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const saveRule = () => {
    if (!ruleForm.sourceQuestionId || !ruleForm.action) {
      toast({
        title: 'Erreur de validation',
        description: 'Veuillez remplir tous les champs obligatoires',
        variant: 'destructive'
      });
      return;
    }

    const newRule: ConditionalRule = {
      id: editingRule?.id || `rule_${Date.now()}`,
      sourceQuestionId: ruleForm.sourceQuestionId!,
      sourceAnswerId: ruleForm.sourceAnswerId || '',
      targetQuestionId: ruleForm.targetQuestionId || '',
      condition: ruleForm.condition!,
      value: ruleForm.value,
      action: ruleForm.action!,
      isActive: ruleForm.isActive!,
      description: ruleForm.description || ''
    };

    let updatedRules;
    if (editingRule) {
      updatedRules = conditionalRules.map(rule => 
        rule.id === editingRule.id ? newRule : rule
      );
    } else {
      updatedRules = [...conditionalRules, newRule];
    }

    onRulesChange(updatedRules);
    setIsDialogOpen(false);
    resetForm();

    toast({
      title: 'Règle sauvegardée',
      description: editingRule ? 'La règle a été mise à jour' : 'Nouvelle règle ajoutée'
    });
  };

  const deleteRule = (ruleId: string) => {
    const updatedRules = conditionalRules.filter(rule => rule.id !== ruleId);
    onRulesChange(updatedRules);
    toast({
      title: 'Règle supprimée',
      description: 'La règle conditionnelle a été supprimée'
    });
  };

  const toggleRuleStatus = (ruleId: string) => {
    const updatedRules = conditionalRules.map(rule =>
      rule.id === ruleId ? { ...rule, isActive: !rule.isActive } : rule
    );
    onRulesChange(updatedRules);
  };

  const getQuestionText = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    return question ? `Q${question.sort_order}: ${question.question_text.substring(0, 50)}...` : 'Question inconnue';
  };

  const getAnswerText = (questionId: string, answerId: string) => {
    const question = questions.find(q => q.id === questionId);
    const answer = question?.answers?.find(a => a.id === answerId);
    return answer ? answer.answer_text.substring(0, 30) + '...' : 'Réponse inconnue';
  };

  const getSourceAnswers = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    return question?.answers || [];
  };

  const validateRules = () => {
    const issues = [];
    
    conditionalRules.forEach(rule => {
      const sourceQuestion = questions.find(q => q.id === rule.sourceQuestionId);
      const targetQuestion = questions.find(q => q.id === rule.targetQuestionId);
      
      if (!sourceQuestion) {
        issues.push(`Règle ${rule.id}: Question source introuvable`);
      }
      
      if (rule.action !== 'end_quiz' && !targetQuestion) {
        issues.push(`Règle ${rule.id}: Question cible introuvable`);
      }
      
      if (rule.sourceAnswerId && sourceQuestion) {
        const answer = sourceQuestion.answers?.find(a => a.id === rule.sourceAnswerId);
        if (!answer) {
          issues.push(`Règle ${rule.id}: Réponse source introuvable`);
        }
      }
    });
    
    return issues;
  };

  const validationIssues = validateRules();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center">
            <GitBranch className="w-5 h-5 mr-2 text-purple-400" />
            Questions Conditionnelles
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Créez des parcours dynamiques basés sur les réponses des apprenants
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
          >
            <Eye size={16} className="mr-2" />
            {previewMode ? 'Édition' : 'Aperçu'}
          </Button>
          <Button
            onClick={() => openEditDialog()}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus size={16} className="mr-2" />
            Nouvelle Règle
          </Button>
        </div>
      </div>

      {/* Validation Issues */}
      {validationIssues.length > 0 && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <h4 className="text-red-400 font-medium mb-2">Problèmes détectés</h4>
                <ul className="text-red-300 text-sm space-y-1">
                  {validationIssues.map((issue, index) => (
                    <li key={index}>• {issue}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rules List */}
      <div className="space-y-4">
        <AnimatePresence>
          {conditionalRules.map((rule, index) => {
            const ActionIcon = actionOptions.find(opt => opt.value === rule.action)?.icon || Settings;
            const actionColor = actionOptions.find(opt => opt.value === rule.action)?.color || 'text-gray-400';
            
            return (
              <motion.div
                key={rule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`${rule.isActive ? 'bg-gray-800/50' : 'bg-gray-800/20'} border-gray-700 hover:border-gray-600 transition-colors`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Badge variant={rule.isActive ? 'default' : 'secondary'} className="text-xs">
                            Règle #{index + 1}
                          </Badge>
                          <ActionIcon className={`w-4 h-4 ${actionColor}`} />
                          <span className="text-sm text-gray-300">
                            {actionOptions.find(opt => opt.value === rule.action)?.label}
                          </span>
                          {!rule.isActive && (
                            <Badge variant="outline" className="text-xs text-gray-500">
                              Désactivée
                            </Badge>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-300 space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-blue-400">Si:</span>
                            <span>{getQuestionText(rule.sourceQuestionId)}</span>
                            {rule.sourceAnswerId && (
                              <>
                                <ArrowRight className="w-3 h-3 text-gray-500" />
                                <span className="text-green-400">{getAnswerText(rule.sourceQuestionId, rule.sourceAnswerId)}</span>
                              </>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <span className="text-purple-400">Alors:</span>
                            <span>{actionOptions.find(opt => opt.value === rule.action)?.label}</span>
                            {rule.targetQuestionId && (
                              <>
                                <ArrowRight className="w-3 h-3 text-gray-500" />
                                <span className="text-yellow-400">{getQuestionText(rule.targetQuestionId)}</span>
                              </>
                            )}
                          </div>
                          
                          {rule.description && (
                            <div className="text-xs text-gray-500 italic mt-2">
                              {rule.description}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={rule.isActive}
                          onCheckedChange={() => toggleRuleStatus(rule.id)}
                          size="sm"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(rule)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit3 size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteRule(rule.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
        
        {conditionalRules.length === 0 && (
          <Card className="bg-gray-800/30 border-gray-700 border-dashed">
            <CardContent className="p-8 text-center">
              <GitBranch className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h4 className="text-gray-400 font-medium mb-2">Aucune règle conditionnelle</h4>
              <p className="text-gray-500 text-sm mb-4">
                Créez des règles pour personnaliser le parcours des apprenants
              </p>
              <Button
                onClick={() => openEditDialog()}
                variant="outline"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
              >
                <Plus size={16} className="mr-2" />
                Créer une règle
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Rule Editor Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center">
              <GitBranch className="w-6 h-6 mr-2 text-purple-400" />
              {editingRule ? 'Modifier la Règle' : 'Nouvelle Règle Conditionnelle'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Source Question */}
            <div>
              <Label className="text-gray-300 mb-2 block">Question source</Label>
              <Select
                value={ruleForm.sourceQuestionId}
                onValueChange={(value) => setRuleForm(prev => ({ 
                  ...prev, 
                  sourceQuestionId: value,
                  sourceAnswerId: '' // Reset answer when question changes
                }))}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Sélectionner une question..." />
                </SelectTrigger>
                <SelectContent>
                  {questions.map(question => (
                    <SelectItem key={question.id} value={question.id}>
                      Q{question.sort_order}: {question.question_text.substring(0, 60)}...
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Answer (if applicable) */}
            {ruleForm.sourceQuestionId && ['equals', 'not_equals', 'contains'].includes(ruleForm.condition!) && (
              <div>
                <Label className="text-gray-300 mb-2 block">Réponse spécifique (optionnel)</Label>
                <Select
                  value={ruleForm.sourceAnswerId}
                  onValueChange={(value) => setRuleForm(prev => ({ ...prev, sourceAnswerId: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Toutes les réponses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Toutes les réponses</SelectItem>
                    {getSourceAnswers(ruleForm.sourceQuestionId).map(answer => (
                      <SelectItem key={answer.id} value={answer.id}>
                        {answer.answer_text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Condition */}
            <div>
              <Label className="text-gray-300 mb-2 block">Condition</Label>
              <Select
                value={ruleForm.condition}
                onValueChange={(value) => setRuleForm(prev => ({ ...prev, condition: value as ConditionalRule['condition'] }))}
              >
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {conditionOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-gray-400">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value (for score conditions) */}
            {['score_above', 'score_below', 'contains'].includes(ruleForm.condition!) && (
              <div>
                <Label className="text-gray-300 mb-2 block">
                  {ruleForm.condition === 'contains' ? 'Texte à rechercher' : 'Valeur de score'}
                </Label>
                <Input
                  type={ruleForm.condition === 'contains' ? 'text' : 'number'}
                  value={ruleForm.value || ''}
                  onChange={(e) => setRuleForm(prev => ({ 
                    ...prev, 
                    value: ruleForm.condition === 'contains' ? e.target.value : parseFloat(e.target.value) 
                  }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder={ruleForm.condition === 'contains' ? 'Texte...' : '0'}
                />
              </div>
            )}

            {/* Action */}
            <div>
              <Label className="text-gray-300 mb-2 block">Action</Label>
              <div className="grid grid-cols-2 gap-3">
                {actionOptions.map(action => {
                  const Icon = action.icon;
                  return (
                    <Card
                      key={action.value}
                      className={`cursor-pointer transition-colors ${
                        ruleForm.action === action.value
                          ? 'bg-purple-500/20 border-purple-500/50'
                          : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                      }`}
                      onClick={() => setRuleForm(prev => ({ ...prev, action: action.value as ConditionalRule['action'] }))}
                    >
                      <CardContent className="p-3 text-center">
                        <Icon className={`w-6 h-6 mx-auto mb-2 ${action.color}`} />
                        <h4 className="text-white text-sm font-medium">{action.label}</h4>
                        <p className="text-gray-400 text-xs">{action.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Target Question (if applicable) */}
            {ruleForm.action && !['end_quiz'].includes(ruleForm.action) && (
              <div>
                <Label className="text-gray-300 mb-2 block">Question cible</Label>
                <Select
                  value={ruleForm.targetQuestionId}
                  onValueChange={(value) => setRuleForm(prev => ({ ...prev, targetQuestionId: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Sélectionner une question..." />
                  </SelectTrigger>
                  <SelectContent>
                    {questions
                      .filter(q => q.id !== ruleForm.sourceQuestionId)
                      .map(question => (
                        <SelectItem key={question.id} value={question.id}>
                          Q{question.sort_order}: {question.question_text.substring(0, 60)}...
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div>
              <Label className="text-gray-300 mb-2 block">Description (optionnel)</Label>
              <Textarea
                value={ruleForm.description}
                onChange={(e) => setRuleForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Décrivez le but de cette règle..."
                rows={2}
              />
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-3">
              <Switch
                checked={ruleForm.isActive}
                onCheckedChange={(checked) => setRuleForm(prev => ({ ...prev, isActive: checked }))}
              />
              <Label className="text-gray-300">Règle active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
            >
              <RotateCcw size={16} className="mr-2" />
              Réinitialiser
            </Button>
            <Button
              onClick={saveRule}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Save size={16} className="mr-2" />
              {editingRule ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { ConditionalQuestions };
